import { Platform } from '@angular/cdk/platform';
import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@env/environment';
import * as Sentry from '@sentry/angular';
import { SentryNonRecordingSpan } from '@sentry/core';
import { catchError, tap, throwError } from 'rxjs';

export const trackingInterceptor: HttpInterceptorFn = (req, next) => {
    const { apiUrl } = environment;
    const isServer = !inject(Platform).isBrowser;
    const tracking = req.url.includes(apiUrl) && !isServer;
    if (!tracking) {
        return next(req);
    }
    const cloneReq = req.clone();
    const activeSpan = Sentry.getActiveSpan();
    const options = {
        name: `{method}: {url}`.replaceWithParams({ method: req.method, url: req.url }),
        op: 'http.client',
        startTime: Date.now(),
        parentSpan: activeSpan,
    };
    const transaction = activeSpan
        ? Sentry.startInactiveSpan(options)
        : new SentryNonRecordingSpan();

    transaction.addLink(req.url);
    transaction.setAttribute('sentry.api.method', req.method);
    transaction.setAttribute('sentry.api.request-header', JSON.stringify(req.headers));

    if (req.body) {
        transaction.setAttribute('sentry.api.request-body', JSON.stringify(req.body));
    }
    if (req.params.keys.length) {
        transaction.setAttribute('sentry.api.request-params', req.params.toString());
    }

    return next(cloneReq).pipe(
        catchError((error) => {
            activeSpan && transaction?.recordException(error);
            return throwError(() => error);
        }),
        tap((event: HttpEvent<unknown>) => {
            if (event instanceof HttpResponse && transaction) {
                transaction.setAttribute('sentry.api.status', event.status);
                transaction.setAttribute(
                    'sentry.api.body',
                    JSON.stringify(event.body).slice(0, 200)
                );
                transaction.end();
            }
        })
    );
};
